import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  // Checkbox,
  InputAdornment,
  // FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Grid from "@mui/material/Grid2";
import useWindowSize from "../../hooks/useWindowSize";
import { get, patch, post } from "../../services/apiService";
import tickMark from "../../assets/images/hosting-plans/tick.png";
import { toast } from "react-toastify";
import CircularLoader from "../../components/common/ScreenLoader";
import BackdropLoader from "../../components/common/BackdropLoader";
import { useAuth } from "../../hooks/useAuth";
import i18next from "i18next";

const HostingPlans = () => {
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState("monthly");
  const { width } = useWindowSize();
  const [editMode, setEditMode] = useState(null);
  const [editedPlan, setEditedPlan] = useState(null);
  const [newFeature, setNewFeature] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [backdropLoading, setBackdropLoading] = useState(false);
  const { currentLang } = useAuth();
  const isArabic = i18next.language === "ar";
  const direction = currentLang === "en" ? "ltr" : "rtl";

  const onLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const onBackdropLoading = (isLoading) => {
    setBackdropLoading(isLoading);
  };

  const fetchPlans = useCallback(async () => {
    try {
      const response = await get(
        "plans/all",
        {
          headers: {
            "Accept-Language": currentLang, // or "en", "fr", etc.
          },
        },
        onLoading
      );
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setIsLoading(false); // Ensure loading state is updated
    }
  }, [currentLang]);

  useEffect(() => {
    setEditMode(null);
    fetchPlans();
  }, [fetchPlans, currentLang]);

  const handleEdit = (id) => {
    const planToEdit = plans.find((plan) => plan.id === id);
    setEditMode(id);
    setEditedPlan({ ...planToEdit }); // Deep clone to avoid modifying the original
  };

  const handleSave = async () => {
    console.log("handle save is called------------->", editedPlan);
    const { planName, prices } = editedPlan;
    const isAllPricesFilled = editedPlan?.prices.every(
      (p) =>
        p.price !== null &&
        p.vat !== null &&
        p.price !== undefined &&
        p.vat !== undefined &&
        p.price !== "" &&
        p.vat !== ""
    );
    const filteredObject = {
      planName,
      planFeatures: editedPlan.features,
      prices,
    };
    if (
      editedPlan.planName &&
      editedPlan?.features?.length > 0 &&
      isAllPricesFilled
    ) {
      const response = await patch(
        `plans/update/${editedPlan.id}`,
        filteredObject,
        {
          headers: {
            "Accept-Language": currentLang, // or "en", "fr", etc.
          },
        },
        onBackdropLoading
      );
      if (response?.status === 200) {
        await fetchPlans();
        toast.success(i18next.t("Plan has been updated successfully"));
        setEditMode(null);
        setEditedPlan(null);
      }
    } else {
      toast.error("Please Enter All Fields");
    }
  };

  const handleInputChange = (field, value) => {
    setEditedPlan((prev) => ({ ...prev, [field]: value }));
  };

  const handlePriceChange = (index, value) => {
    const updatedPrices = [...editedPlan.prices];
    updatedPrices[index].price = value;
    console.log("updated prices----------->", updatedPrices);
    setEditedPlan((prev) => ({ ...prev, prices: updatedPrices }));
  };

  const handleVatChange = (index, value) => {
    const updatedPrices = [...editedPlan.prices];
    updatedPrices[index].vat = value;
    console.log("updated prices----------->", updatedPrices);
    setEditedPlan((prev) => ({ ...prev, prices: updatedPrices }));
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...editedPlan.features];
    updatedFeatures[index].planFeature = value;
    setEditedPlan((prev) => ({ ...prev, features: updatedFeatures }));
  };

  const handleAddFeature = async () => {
    if (!newFeature.trim() && newFeature?.length === 0) return;
    const newFeatureObject = {
      id: editedPlan?.features?.length + 1,
      planFeature: newFeature,
    };
    const response = await post(
      `plans/create-feature/${editedPlan.id}`,
      {
        planFeature: newFeature,
      },
      {},
      onBackdropLoading
    );
    if (response.status === 201) {
      console.log("old features--------->", editedPlan.planFeature);
      console.log("new feature object----------->", newFeatureObject);
      const updatedFeatures = [...editedPlan.features];
      updatedFeatures.push(newFeatureObject);
      setEditedPlan((prev) => ({ ...prev, features: updatedFeatures }));
      setNewFeature("");
    }
  };

  return (
    <Box sx={{ p: width > 450 ? 3 : 1 }} dir={direction}>
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between", // Distribute items to both ends
          p: 2,
          marginBottom: "2%",
        }}
      >
        {/* Left-Aligned Heading */}
        {backdropLoading && <BackdropLoader />}
        <Box>
          <Typography
            sx={{
              fontFamily: "Tajawal-Bold",
              color: "#0A0A0A",
              fontSize: width > 900 ? "26px" : "18px",
              fontWeight: 700,
            }}
          >
            {i18next.t("Hosting Plans")}
          </Typography>
        </Box>
      </Card>
      <Card
        sx={{
          backgroundColor: "rgba(255, 255, 255, 1)",
          borderRadius: "16px",
          height: "auto",
          width: "100%",
          border: "1px solid rgba(239, 244, 250, 1)",
        }}
      >
        {isLoading ? (
          <Grid
            item
            size={{ xs: 12 }}
            sx={{ textAlign: "center", height: "500px", width: "100%" }}
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularLoader />
            </Box>
          </Grid>
        ) : plans?.length > 0 ? (
          <>
            <Box mt={5} textAlign="center" sx={{display:"flex", justifyContent:"center",gap:3}}>
              <Button
                variant="outlined"
                sx={{
                  backgroundColor:
                    activePlan === "monthly" ? "#3173B1" : "white",
                  color: activePlan === "monthly" ? "white" : "black",
                  width: width > 600 ? "200px" : "150px",
                }}
                onClick={() => setActivePlan("monthly")}
              >
                {i18next.t("Billed Monthly")}
              </Button>
              <Button
                variant="outlined"
                sx={{
                  backgroundColor:
                    activePlan === "yearly" ? "#3173B1" : "white",
                  color: activePlan === "yearly" ? "white" : "black",
                  width: width > 600 ? "200px" : "150px",
                }}
                onClick={() => setActivePlan("yearly")}
              >
                {i18next.t("Billed Yearly")}
              </Button>
            </Box>
            <Grid
              container
              spacing={{ xl: 2, lg: 2, md: 1, sm: 2, xs: 1 }}
              mt={3}
              sx={{ py: { xl: 4, lg: 3, sm: 2 } }}
            >
              {plans?.map((plan) => (
                <Grid
                  item
                  size={{ xl: 4, lg: 6, md: 6, sm: 12, xs: 12 }}
                  key={plan.id}
                  sx={{
                    px: { xs: 2 },
                    py: { xs: 2 },
                    width: "100%",
                    // background: "orange",
                  }}
                >
                  <Card
                    sx={{
                      height: {
                        xl: "450px",
                        lg: "450px",
                        md: "430px",
                        sm: "430px",
                      },
                      overflowY: "auto",
                      width: width > 600 ? "100%" : "400px",
                      // background: "grey",
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          onClick={() =>
                            editMode === plan.id
                              ? handleSave()
                              : handleEdit(plan.id)
                          }
                        >
                          {editMode === plan.id ? <SaveIcon /> : <EditIcon />}
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          textAlign: editMode !== plan.id && "center",
                          justifyContent: editMode !== plan.id && "center",
                          alignItems: editMode !== plan.id && "center",
                        }}
                      >
                        {editMode === plan.id ? (
                          <>
                            <Typography
                              sx={{
                                fontFamily: "Tajawal-Black",
                                marginBottom: "2%",
                                fontSize:
                                  width > 1200
                                    ? "24px"
                                    : width > 900
                                      ? "20px"
                                      : "18px",
                                lineHeight: "32px",
                                fontWeight: 700,
                              }}
                            >
                              Plan Title
                            </Typography>
                            <TextField
                              value={editedPlan.planName}
                              onChange={(e) =>
                                handleInputChange("planName", e.target.value)
                              }
                              fullWidth
                            />
                          </>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: "Tajawal-Black",
                              marginBottom: "10%",
                              fontSize:
                                width > 1440
                                  ? "24px"
                                  : width > 1200
                                    ? "22px"
                                    : width > 900
                                      ? "20px"
                                      : width > 600
                                        ? "18px"
                                        : "17px",
                              lineHeight: "32px",
                              fontWeight: 700,
                            }}
                          >
                            {plan.planName}
                          </Typography>
                        )}
                        {editMode === plan.id ? (
                          <>
                            <Typography
                              sx={{
                                fontFamily: "Tajawal-Black",
                                marginBottom: "2%",
                                marginTop: "2%",
                                fontSize:
                                  width > 1440
                                    ? "24px"
                                    : width > 1200
                                      ? "22px"
                                      : width > 900
                                        ? "20px"
                                        : width > 600
                                          ? "18px"
                                          : "17px",
                                lineHeight: "32px",
                                fontWeight: 700,
                              }}
                            >
                              Pricing
                            </Typography>
                            <TextField
                              type="number"
                              value={
                                activePlan === "monthly"
                                  ? editedPlan?.prices[0]?.price
                                  : editedPlan?.prices[1]?.price
                              }
                              onChange={(e) =>
                                handlePriceChange(
                                  activePlan === "monthly" ? 0 : 1,
                                  e.target.value
                                )
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    USD
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                            />
                          </>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: "Tajawal-Black",
                              fontSize:
                                width > 1440
                                  ? "30px"
                                  : width > 1200
                                    ? "28px"
                                    : width > 900
                                      ? "26px"
                                      : width > 600
                                        ? "24px"
                                        : "22px",
                              lineHeight: "32px",
                              fontWeight: 700,
                            }}
                          >
                            {activePlan === "monthly"
                              ? `USD ${plan.prices[0]?.price} / ${i18next.t("Monthly")}`
                              : `USD ${plan.prices[1]?.price} / ${i18next.t("Yearly")}`}
                          </Typography>
                        )}
                        {editMode === plan.id ? (
                          <>
                            <Typography
                              sx={{
                                fontFamily: "Tajawal-Black",
                                marginBottom: "2%",
                                marginTop: "2%",
                                fontSize:
                                  width > 1440
                                    ? "24px"
                                    : width > 1200
                                      ? "22px"
                                      : width > 900
                                        ? "20px"
                                        : width > 600
                                          ? "18px"
                                          : "17px",
                                lineHeight: "32px",
                                fontWeight: 700,
                              }}
                            >
                              VAT
                            </Typography>
                            <TextField
                              type="number"
                              value={
                                activePlan === "monthly"
                                  ? Number(editedPlan?.prices[0]?.vat)
                                  : Number(editedPlan?.prices[1]?.vat)
                              }
                              onChange={(e) =>
                                handleVatChange(
                                  activePlan === "monthly" ? 0 : 1,
                                  e.target.value
                                )
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    USD
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                            />
                          </>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              marginTop: "2%",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Tajawal-Black",
                                fontSize:
                                  width > 1440
                                    ? "22px"
                                    : width > 1200
                                      ? "20px"
                                      : width > 900
                                        ? "18px"
                                        : width > 600
                                          ? "15px"
                                          : "22px",
                                lineHeight: "32px",
                                fontWeight: 400,
                                marginRight: "2%",
                              }}
                            >
                              VAT
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Tajawal-Black",
                                fontSize:
                                  width > 1440
                                    ? "22px"
                                    : width > 1200
                                      ? "20px"
                                      : width > 900
                                        ? "18px"
                                        : width > 600
                                          ? "15px"
                                          : "22px",
                                lineHeight: "32px",
                                fontWeight: 400,
                              }}
                            >
                              {activePlan === "monthly"
                                ? `USD ${plan.prices[0]?.vat} / ${i18next.t("Monthly")}`
                                : `USD ${plan.prices[1]?.vat} / ${i18next.t("Yearly")}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* </Box>} */}
                      {editMode === plan.id ? (
                        <>
                          <Typography
                            sx={{
                              fontFamily: "Tajawal-Black",
                              marginTop: "2%",
                              fontSize:
                                width > 1440
                                  ? "24px"
                                  : width > 1200
                                    ? "22px"
                                    : width > 900
                                      ? "20px"
                                      : width > 600
                                        ? "18px"
                                        : "17px",
                              lineHeight: "32px",
                              fontWeight: 700,
                            }}
                          >
                            Content
                          </Typography>
                          {editedPlan?.features?.map(
                            (feature, index) =>
                              feature?.planFeature && (
                                <TextField
                                  key={feature?.id}
                                  value={feature?.planFeature}
                                  onChange={(e) =>
                                    handleFeatureChange(index, e.target.value)
                                  }
                                  fullWidth
                                  sx={{ mb: 2 }}
                                />
                              )
                          )}
                        </>
                      ) : (
                        plan?.features?.map(
                          (feature) =>
                            feature?.planFeature?.length > 0 && (
                              <Box
                                key={feature.id}
                                sx={{
                                  width: "100%",
                                  mt: 3,
                                  display: "flex",
                                
                                  justifyContent: isArabic
                                    ? "flex-start"
                                    : "flex-start",
                                  alignItems: "center",
                                  gap: 1.5,
                                  px: isArabic ? 0 : { xl: 4.5, lg: 4 }, // Remove horizontal padding in Arabic
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={tickMark}
                                    alt="tick"
                                    style={{
                                      height: width > 600 ? "17px" : "12px",
                                      width: width > 600 ? "17px" : "12px",
                                    }}
                                  />
                                </Box>

                                <Typography
                                  sx={{
                                    mt: 1,
                                    color: "rgba(128, 128, 128, 1)",
                                    fontFamily: "Tajawal-Regular",
                                    fontSize:
                                      width > 1440
                                        ? "16px"
                                        : width > 1200
                                          ? "15px"
                                          : width > 900
                                            ? "13px"
                                            : width > 600
                                              ? "12px"
                                              : "11.5px",
                                    fontWeight: "700",
                                    display: "flex",
                                    alignItems: "center",
                                    textAlign: isArabic ? "right" : "left",
                                  }}
                                >
                                  {feature?.planFeature}
                                </Typography>
                              </Box>
                            )
                        )
                      )}
                      {editMode === plan?.id && (
                        <Box sx={{ display: "flex" }}>
                          <TextField
                            value={newFeature}
                            onChange={(e) => setNewFeature(e.target.value)}
                            placeholder="Add new feature"
                            fullWidth
                          />
                          <Button onClick={handleAddFeature}>Add</Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Grid item size={{ xs: 12 }} sx={{ height: "100%", width: "100%" }}>
            <Box
              sx={{
                height: "500px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontFamily: "Tajawal-Bold",
                    fontWeight: 600,
                    fontSize:
                      width > 1440
                        ? "24px"
                        : width > 1200
                          ? "22px"
                          : width > 900
                            ? "20px"
                            : width > 600
                              ? "18px"
                              : "17px",
                  }}
                >
                  Hosting plan lists is empty.
                </Typography>
                <Typography
                  sx={{
                    marginTop: "3%",
                    textAlign: "center",
                    fontFamily: "Tajawal-Bold",
                    fontWeight: 700,
                    fontSize: "22px",
                    color: "#00000099",
                  }}
                >
                  In the hosting plan lists, you will get the monthly and yearly
                  plans.
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Card>
    </Box>
  );
};

export default HostingPlans;
